/**
 * ai-context命令 - AI上下文管理（简化版）
 * 专注于解决"AI无状态"问题
 */

import { Command } from 'commander';
import { logger } from '../utils/logger.js';
import { ContextInjector } from '../claude/context-injector.js';
import { hookSystem } from '../claude/hook-system.js';
import pc from 'picocolors';

export function createAIContextCommand(): Command {
  const command = new Command('ai-context')
    .aliases(['ctx'])
    .description('管理AI工作上下文（解决AI无状态问题）')
    .addHelpText('after', `
示例:
  workflow ai-context generate    生成最新上下文
  workflow ai-context show        显示当前上下文
  workflow ai-context update      更新上下文
  workflow ctx                    快捷方式：生成上下文
`);

  // 默认动作：生成上下文
  command.action(async () => {
    try {
      const injector = new ContextInjector();
      logger.info('🧠 正在生成AI上下文...');
      await injector.generateContext();
      logger.success('✅ 上下文生成成功！AI现在可以了解完整的项目状态了。');
    } catch (error: any) {
      logger.error('生成上下文失败:', error.message);
      process.exit(1);
    }
  });

  // 生成上下文
  command
    .command('generate')
    .aliases(['gen', 'g'])
    .description('生成AI工作上下文')
    .option('-q, --quiet', '静默模式，减少输出')
    .action(async (options) => {
      try {
        const injector = new ContextInjector();
        
        if (!options.quiet) {
          logger.info('🧠 正在生成AI上下文...');
        }
        
        await injector.generateContext();
        
        // 触发Hook
        await hookSystem.executeHook('on_context_update', {
          type: 'context_generated',
          timestamp: new Date()
        });
        
        if (!options.quiet) {
          logger.success('✅ 上下文生成成功！');
          console.log('');
          console.log('📍 上下文文件位置:');
          console.log(`  - ${pc.cyan('.claude/CONTEXT.md')}`);
          console.log(`  - ${pc.cyan('.workflow/CURRENT_CONTEXT.md')}`);
          console.log('');
          console.log('💡 提示: AI现在可以访问完整的项目上下文了！');
        }
      } catch (error: any) {
        logger.error('生成上下文失败:', error.message);
        process.exit(1);
      }
    });

  // 显示上下文
  command
    .command('show')
    .aliases(['s'])
    .description('显示当前AI上下文')
    .action(async () => {
      try {
        const injector = new ContextInjector();
        await injector.showContext();
      } catch (error: any) {
        logger.error('显示上下文失败:', error.message);
        process.exit(1);
      }
    });

  // 更新上下文
  command
    .command('update')
    .aliases(['u'])
    .description('更新AI上下文的特定部分')
    .option('--tasks', '只更新任务信息')
    .option('--files', '只更新文件变更')
    .option('--dependencies', '只更新依赖信息')
    .option('--all', '更新所有内容（默认）')
    .action(async (options) => {
      try {
        const injector = new ContextInjector();
        
        const parts: string[] = [];
        if (options.tasks) parts.push('tasks');
        if (options.files) parts.push('files');
        if (options.dependencies) parts.push('dependencies');
        
        if (parts.length === 0 || options.all) {
          // 如果没有指定部分或指定了all，则更新全部
          logger.info('🔄 更新完整上下文...');
          await injector.generateContext();
        } else {
          logger.info(`🔄 更新上下文: ${parts.join(', ')}`);
          await injector.updateContext(parts);
        }
        
        logger.success('✅ 上下文更新成功！');
      } catch (error: any) {
        logger.error('更新上下文失败:', error.message);
        process.exit(1);
      }
    });

  // 自动更新设置
  command
    .command('auto')
    .description('配置上下文自动更新')
    .option('--enable', '启用自动更新')
    .option('--disable', '禁用自动更新')
    .option('--interval <minutes>', '设置更新间隔（分钟）', '5')
    .action(async (options) => {
      try {
        if (options.enable) {
          // TODO: 实现自动更新机制
          logger.info('🤖 启用上下文自动更新...');
          logger.info(`更新间隔: ${options.interval} 分钟`);
          logger.warn('⚠️  自动更新功能即将推出');
        } else if (options.disable) {
          logger.info('🚫 禁用上下文自动更新');
          logger.warn('⚠️  自动更新功能即将推出');
        } else {
          logger.info('请指定 --enable 或 --disable');
        }
      } catch (error: any) {
        logger.error('配置自动更新失败:', error.message);
        process.exit(1);
      }
    });

  // 保存上下文快照
  command
    .command('save')
    .description('保存当前上下文快照')
    .option('-n, --name <name>', '快照名称')
    .action(async (options) => {
      try {
        const name = options.name || `snapshot-${Date.now()}`;
        logger.info(`💾 保存上下文快照: ${name}`);
        
        // TODO: 实现快照功能
        logger.warn('⚠️  快照功能即将推出');
      } catch (error: any) {
        logger.error('保存快照失败:', error.message);
        process.exit(1);
      }
    });

  return command;
}